import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  AlertTriangle, ChevronDown, Copy, Grid3x3, Image as ImageIcon, ImageOff, Loader2, MapPin, Maximize2, Minimize2, X,
  type LucideIcon,
} from 'lucide-react';
import { toast, useWorldsStore } from '../worlds.store';
import { TP_ALTITUDE, loadPref, savePref } from '../constants';
import { withGameGroup } from '../sharedMap';
import type { WorldMap } from './useWorldMap';
import ws from '../WorldsPage.module.scss';
import fs from './MapFrame.module.scss';

// ============================================================
// Cadre commun des cartes : conteneur Leaflet, coordonnées, plein écran, clic droit
// ============================================================
// Le plein écran est en CSS (position fixed) et non requestFullscreen : les toasts du panel
// restent visibles pendant qu'on peint.
// Surimpressions communes aux onglets Carte et Marqueurs : chargement de la grille (au centre),
// note du fond quand le niveau n'a pas d'URL de tuiles (« image fixe » du terrain s'il en a une
// et que la couche est allumée, sinon « fond neutre » ; pastille discrète pendant son premier
// téléchargement et si l'image manque),
// avertissements (tuiles introuvables, contenu mixte, grille en échec) et rappel « Échap » en
// plein écran (en haut à gauche, à droite du zoom de Leaflet).
// ============================================================

/** Au-delà de ce nombre de tuiles en erreur sans aucune tuile chargée, l'URL est suspecte. */
const TILE_ERROR_ALERT = 8;
/** Note du fond (neutre ou image fixe) repliée (préférence du navigateur). */
const NOTE_PREF = 'worlds.map.noteCollapsed';
/** Même seuil que le mixin mobile du panel ($breakpoint-md). */
const NARROW_QUERY = '(max-width: 768px)';

async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    // Contexte non sécurisé ou permission refusée : on laisse copier à la main.
    window.prompt('Copier la commande :', text);
    return false;
  }
}

export function MapFrame({ wm, side, overlay, onEscape }: {
  wm: WorldMap;
  /** Colonne de droite (outils, pays actif, couches). */
  side?: ReactNode;
  /** Éléments posés sur la carte (badge « En cours », aide du mode). */
  overlay?: ReactNode;
  /** Échap : renvoie true si l'appelant l'a consommé (geste annulé), sinon on quitte le plein écran. */
  onEscape?: () => boolean;
}) {
  const gridError = useWorldsStore((s) => s.gridError);
  const gridLoading = useWorldsStore((s) => !!s.grid && !s.grid.loaded);
  const [params, setParams] = useSearchParams();
  const { menu, closeMenu, fullscreen, setFullscreen } = wm;
  const menuRef = useRef<HTMLDivElement | null>(null);
  // Sans choix mémorisé, la note part repliée sur un écran étroit : dépliée, elle couvrirait une
  // bonne part d'une carte de 320 px.
  const [noteCollapsed, setNoteCollapsed] = useState(() => {
    const saved = loadPref<boolean | null>(NOTE_PREF, null);
    if (typeof saved === 'boolean') return saved;
    try { return window.matchMedia(NARROW_QUERY).matches; } catch { return false; }
  });

  // Le menu du clic droit se ferme aussi sur un clic hors de lui (la carte le ferme déjà seule).
  useEffect(() => {
    if (!menu) return;
    const onDown = (e: MouseEvent) => { if (!menuRef.current?.contains(e.target as Node)) closeMenu(); };
    document.addEventListener('mousedown', onDown, true);
    // Focus sur la première entrée : Entrée copie, Échap ferme.
    menuRef.current?.querySelector<HTMLButtonElement>('[role="menuitem"]')?.focus({ preventScroll: true });
    return () => document.removeEventListener('mousedown', onDown, true);
  }, [menu, closeMenu]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (menu) { closeMenu(); return; }
      if (onEscape?.()) return;
      if (fullscreen) setFullscreen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [menu, closeMenu, fullscreen, setFullscreen, onEscape]);

  const copy = async () => {
    if (!menu) return;
    const cmd = `/tp ${menu.x} ${TP_ALTITUDE} ${menu.z}`;
    closeMenu();
    if (await copyText(cmd)) toast.success(`Copié : ${cmd}`);
  };

  const toggleNote = () => {
    const next = !noteCollapsed;
    setNoteCollapsed(next);
    savePref(NOTE_PREF, next);
  };

  const tileUrl = wm.level?.tileUrl ?? null;
  const mixed = !!tileUrl?.startsWith('http://') && window.location.protocol === 'https:';
  // Des erreurs sur des zones sans rendu sont normales : seule une URL qui ne sert AUCUNE tuile
  // est signalée.
  const tilesMissing = !!tileUrl && !mixed && wm.tileErrors >= TILE_ERROR_ALERT && wm.tileLoads === 0;
  const fsLabel = fullscreen ? 'Quitter le plein écran (Échap)' : 'Afficher la carte en plein écran';
  const bg = wm.staticBg;
  // Note « image fixe » dès le chargement (le quadrillage reste dessiné jusqu'à l'image), avec la
  // pastille « Chargement du fond… » tant qu'elle n'est pas arrivée.
  const bgShown = !!bg && (wm.staticBgState === 'loading' || wm.staticBgState === 'ok');
  const levelsLink = (
    <button type="button" className={`${ws.linkBtn} ${fs.noteLink}`} onClick={() => setParams(withGameGroup({ tab: 'niveaux' }, params))}>
      Mondes (niveaux)
    </button>
  );
  const note = (Icon: LucideIcon, short: string, title: string, body: ReactNode) => (
    noteCollapsed ? (
      <button type="button" className={`${ws.mapNote} ${fs.noteBtn}`} onClick={toggleNote} title={title} aria-expanded={false}>
        <Icon size={13} aria-hidden /> {short} <ChevronDown size={12} aria-hidden />
      </button>
    ) : (
      <div className={ws.mapNote} role="note">
        <Icon size={13} aria-hidden />
        <span>{body}</span>
        <button type="button" className={fs.noteClose} onClick={toggleNote} title="Réduire la note"
          aria-label="Réduire la note" aria-expanded>
          <X size={12} aria-hidden />
        </button>
      </div>
    )
  );

  return (
    <div className={`${ws.mapShell} ${fullscreen ? ws.mapShellFull : ''}`}>
      <div className={ws.mapMain}>
        <div ref={wm.containerRef} className={ws.map} />
        <div className={ws.mapTopRight}>
          <span ref={wm.coordsRef} className={ws.coords} aria-live="off" />
          <button type="button" className={ws.mapBtn} onClick={() => setFullscreen(!fullscreen)}
            title={fsLabel} aria-label={fsLabel} aria-pressed={fullscreen}>
            {fullscreen ? <Minimize2 size={14} aria-hidden /> : <Maximize2 size={14} aria-hidden />}
          </button>
        </div>

        <div className={ws.mapTopLeft}>
          {!tileUrl && wm.level && (bgShown && bg
            ? note(ImageIcon, 'Image fixe',
              `Fond : image fixe du terrain ${bg.origin} en attendant les tuiles. Cliquer pour le détail.`,
              <>
                Fond : image fixe du terrain {bg.origin} en attendant les tuiles (renseigner tileUrl dans{' '}
                {levelsLink} pour des tuiles).
              </>)
            : note(Grid3x3, 'Fond neutre', 'Fond neutre : aucune URL de tuiles pour ce niveau. Cliquer pour le détail.',
              <>Fond neutre : aucune tuile pour ce niveau. URL à renseigner dans l'onglet {levelsLink}.</>))}
          {bg && wm.staticBgState === 'loading' && (
            <span className={fs.bgLoading} role="status" title={`${bg.label} : ${bg.url}`}>
              <Loader2 size={12} className={ws.spinning} aria-hidden /> Chargement du fond…
            </span>
          )}
          {bg && wm.staticBgState === 'error' && (
            <span className={fs.bgMissing} role="status" title={`${bg.label} : ${bg.url}`}>
              <ImageOff size={12} aria-hidden /> Image de fond introuvable
            </span>
          )}
          {gridError && (
            <div className={ws.mapWarn} role="alert"><AlertTriangle size={13} aria-hidden /> Grille : {gridError}</div>
          )}
          {mixed && (
            <div className={ws.mapWarn} role="alert">
              <AlertTriangle size={13} aria-hidden />
              Tuiles en http sur une page https : le navigateur les bloque (contenu mixte). Utilise une URL https.
            </div>
          )}
          {tilesMissing && (
            <div className={ws.mapWarn} role="alert" title={tileUrl ?? undefined}>
              <AlertTriangle size={13} aria-hidden />
              Tuiles introuvables à cette URL ({wm.tileErrors.toLocaleString('fr-FR')} erreurs, aucune tuile chargée).
              Vérifie l'URL du niveau.
            </div>
          )}
          {fullscreen && <span>Échap pour quitter le plein écran</span>}
        </div>

        {gridLoading && !gridError && (
          <div className={ws.mapLoading} role="status">
            <span><Loader2 size={32} className={ws.spinning} aria-hidden /> Chargement de la grille…</span>
          </div>
        )}

        {overlay}
        {menu && (
          <div ref={menuRef} className={ws.ctxMenu} style={{ left: menu.left, top: menu.top }} role="menu"
            aria-label={`Position X ${menu.x}, Z ${menu.z}`} onContextMenu={(e) => e.preventDefault()}>
            <div className={ws.ctxHead}><MapPin size={13} aria-hidden /> X {menu.x}, Z {menu.z}</div>
            <button type="button" role="menuitem" onClick={() => void copy()}>
              <Copy size={13} aria-hidden /> Copier les coordonnées
              <span className={ws.ctxHint}>/tp {menu.x} {TP_ALTITUDE} {menu.z}</span>
            </button>
          </div>
        )}
      </div>
      {side && <aside className={ws.mapSide}>{side}</aside>}
    </div>
  );
}
